import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Paper,
  CircularProgress, Alert, TextField, Pagination, InputAdornment, Typography, Chip, Fade, Tooltip, Badge,
} from "@mui/material";
import {
  ExpandMore, ExpandLess, Dangerous as DangerousIcon, Info as InfoIcon, Search as SearchIcon,
  ArrowDropUp, ArrowDropDown, CalendarToday, MoreHoriz, AccessTime,
} from "@mui/icons-material";
import useAxios from "../../hooks/useAxios";
import AuthContext from "../../contexts/auth-context";
import AppContext from "../../contexts/app-context";
import { urls } from "../config/variables";
import { validatePatientAbuseTable } from "../../Utils/validateHttpResponse";

const GROUP_COLORS = ["#ffffffff", "#EAF2FF"];
const TABLE_HEADERS = [
  { label: "Patient Name", key: "patient_name", width: "18%" },
  { label: "Provider", key: "provider", width: "14%" },
  { label: "Diagnosis", key: "diagnosis", width: "24%" },
  { label: "Drug", key: "drug_brand_name", width: "24%" },
  { label: "Count", key: "count", width: "9%" },
  { label: "Date & Time", key: "encounter_date", width: "15%" },
];

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return {
    date: date.toISOString().split("T")[0],
    time: date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }),
  };
};

const EmptyIndicator = ({ color = "#be5858ff", iconColor = "#ffffffff" }) => (
  <Box sx={{
    display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28,
    borderRadius: "50%", backgroundColor: color, color: iconColor, fontSize: "13px", fontWeight: 600,
    transition: "all 0.2s ease", "&:hover": { transform: "scale(1.1)" },
  }}>−</Box>
);

const DateTimeItem = ({ date, time, isFirst = false }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <CalendarToday sx={{ fontSize: isFirst ? 14 : 12, color: isFirst ? "#3B82F6" : "#9CA3AF" }} />
      <Typography sx={{ fontSize: isFirst ? 14 : 13, color: isFirst ? "#374151" : "#6B7280", fontWeight: 600 }}>{date}</Typography>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 2.5 }}>
      <AccessTime sx={{ fontSize: isFirst ? 12 : 11, color: "#9CA3AF" }} />
      <Typography sx={{ fontSize: isFirst ? 13 : 12, color: isFirst ? "#6B7280" : "#9CA3AF", fontWeight: 500 }}>{time}</Typography>
    </Box>
  </Box>
);

const DateTimeDisplay = ({ dates, rowKey, isExpanded, onToggle }) => {
  if (dates.length === 0) return <Typography sx={{ fontSize: 14, color: "#9CA3AF" }}>−</Typography>;
  const { date: firstDate, time: firstTime } = formatDateTime(dates[0]);
  if (dates.length === 1) return <DateTimeItem date={firstDate} time={firstTime} isFirst />;

  return (
    <Box>
      <Tooltip title={isExpanded ? "Click to collapse" : "Click to see all dates & times"} arrow>
        <Box onClick={(e) => { e.stopPropagation(); onToggle(rowKey); }} sx={{
          display: "inline-flex", flexDirection: "column", gap: 0.5, cursor: "pointer", padding: "6px 10px",
          borderRadius: "8px", transition: "all 0.2s ease", "&:hover": { backgroundColor: "rgba(59,130,246,0.1)" },
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: 14, color: "#3B82F6" }} />
            <Typography sx={{ fontSize: 14, color: "#374151", fontWeight: 600 }}>{firstDate}</Typography>
            <Badge badgeContent={`+${dates.length - 1}`} color="primary"
              sx={{ ml: 0.5, "& .MuiBadge-badge": { fontSize: 10, height: 18, minWidth: 18 } }}>
              <MoreHoriz sx={{ fontSize: 18, color: "#3B82F6" }} />
            </Badge>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 2.5 }}>
            <AccessTime sx={{ fontSize: 12, color: "#9CA3AF" }} />
            <Typography sx={{ fontSize: 13, color: "#6B7280", fontWeight: 500 }}>{firstTime}</Typography>
          </Box>
        </Box>
      </Tooltip>
      {isExpanded && (
        <Fade in={isExpanded} timeout={300}>
          <Box sx={{ mt: 1, pl: 2, borderLeft: "3px solid #3B82F6", py: 0.5 }}>
            {dates.slice(1).map((date, idx) => {
              const { date: d, time: t } = formatDateTime(date);
              return <Box key={idx} sx={{ py: 0.5 }}><DateTimeItem date={d} time={t} /></Box>;
            })}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

const PatientEncountersTable = () => {
  const { token } = useContext(AuthContext);
  const { globalFilterQuery = "" } = useContext(AppContext) || {};
  const { operation } = useAxios();
  const [tableData, setTableData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [tableDataError, setTableDataError] = useState(undefined);
  const [expanded, setExpanded] = useState([]);
  const [expandedDates, setExpandedDates] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const [sortBy, setSortBy] = useState("patient_name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 8;

  useEffect(() => {
    setLoadingData(true);
    setTableDataError(undefined);
    operation(
      { method: "GET", url: urls.PATIENT_ABUSE_TABLE + globalFilterQuery, headers: { Authorization: `token ${token}` } },
      (response) => {
        setLoadingData(false);
        validatePatientAbuseTable(response) ? setTableData(response || []) : setTableDataError("Invalid response format.");
      },
      (error) => { setLoadingData(false); setTableDataError(error); }
    );
  }, []); // eslint-disable-line

  const handleExpandClick = (index) => setExpanded((prev) => prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]);
  const toggleDateExpansion = (rowKey) => setExpandedDates((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  const handleSort = (columnKey) => {
    if (sortBy === columnKey) setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else { setSortBy(columnKey); setSortDirection("asc"); }
  };

  const filteredData = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    let filtered = (tableData || []).filter((patient) => {
      const recordsString = (patient.patient_records || []).map((r) =>
        `${r.provider} ${r.drug_brand_name || ""} ${r.diagnosis || ""} ${Array.isArray(r.encounter_date) ? r.encounter_date.join(" ") : r.encounter_date} ${r.count || ""}`
      ).join(" ").toLowerCase();
      return (patient.patient_name || "").toLowerCase().includes(search) || recordsString.includes(search);
    });

    filtered.sort((a, b) => {
      let result = 0;
      if (sortBy === "patient_name") result = (a.patient_name || "").localeCompare(b.patient_name || "");
      else if (sortBy === "count") result = (a.patient_records || []).reduce((sum, r) => sum + (r.count || 0), 0) - (b.patient_records || []).reduce((sum, r) => sum + (r.count || 0), 0);
      else {
        const getFieldValue = (patient, key) => {
          const values = (patient.patient_records || []).map((r) => r[key]).filter(Boolean).flat();
          if (values.length === 0) return "";
          if (key === "encounter_date") return values.sort((d1, d2) => new Date(d2) - new Date(d1))[0];
          return values.sort()[0];
        };
        const aVal = getFieldValue(a, sortBy), bVal = getFieldValue(b, sortBy);
        result = sortBy === "encounter_date" ? new Date(aVal) - new Date(bVal) : String(aVal).localeCompare(String(bVal));
      }
      return sortDirection === "asc" ? result : -result;
    });
    return filtered;
  }, [tableData, searchValue, sortBy, sortDirection]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const currentRows = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages || 1); }, [totalPages, currentPage]);

  if (loadingData) return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: 450, gap: 2, p: 2 }}>
      <CircularProgress size={50} thickness={4} />
      <Typography sx={{ color: "#6B7280", fontSize: 14 }}>Loading patient data...</Typography>
    </Box>
  );

  if (tableDataError) return (
    <Box sx={{ p: 2 }}>
      <Alert icon={<DangerousIcon />} severity="error" sx={{ borderRadius: "12px" }}>
        Something went wrong. Please try again later.
      </Alert>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, gap: 2 }}>
        <TextField label="Search patients, providers, drugs..." variant="outlined" size="medium" value={searchValue}
          onChange={(e) => { setSearchValue(e.target.value); setCurrentPage(1); }}
          sx={{ minWidth: 350, "& .MuiOutlinedInput-root": { borderRadius: "12px", backgroundColor: "#fff", transition: "all 0.3s ease",
            "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }, "&.Mui-focused": { boxShadow: "0 4px 16px rgba(25,118,210,0.15)" } } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#9CA3AF" }} /></InputAdornment> }}
        />
        {filteredData.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ color: "#6B7280", fontSize: 14 }}>
              Showing {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, filteredData.length)} of {filteredData.length}
            </Typography>
            {filteredData.length > rowsPerPage && (
              <Pagination page={currentPage} count={totalPages} onChange={(e, v) => setCurrentPage(v)} color="primary" shape="rounded"
                sx={{ "& .MuiPaginationItem-root": { borderRadius: "8px", fontWeight: 500 } }} />
            )}
          </Box>
        )}
      </Box>

      {filteredData.length === 0 ? (
        <Alert icon={<InfoIcon />} severity="info" sx={{ borderRadius: "12px" }}>
          No suspicious activity found {searchValue && "for your search"}
        </Alert>
      ) : (
        <Paper elevation={0} sx={{ overflow: "hidden", borderRadius: "16px", border: "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader sx={{ borderCollapse: "separate", borderSpacing: 0, minWidth: "100%", tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  {TABLE_HEADERS.map((header, idx) => (
                    <TableCell key={header.key} onClick={() => handleSort(header.key)}
                      sx={{
                        fontWeight: 700, fontSize: 13, color: "#374151", cursor: "pointer", backgroundColor: "#F9FAFB",
                        borderBottom: "4px solid #E5E7EB", borderRight: idx < TABLE_HEADERS.length - 1 ? "1px solid #E5E7EB" : "none",
                        textTransform: "uppercase", letterSpacing: "0.5px", py: 2, transition: "all 0.2s ease",
                        width: header.width, zIndex: 7, ...(idx === 0 && { position: "sticky", left: 0 }),
                        ...(header.key === "count" && { textAlign: "center" }), "&:hover": { backgroundColor: "#F3F4F6" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {header.label}
                        {sortBy === header.key && (
                          <Box sx={{ display: "flex", alignItems: "center", color: "#3B82F6" }}>
                            {sortDirection === "asc" ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {currentRows.map((patient, index) => {
                  const records = [...(patient.patient_records || [])].sort((a, b) => String(a.record_uuid || "").localeCompare(String(b.record_uuid || "")));
                  const isExpanded = expanded.includes(index);
                  const colorByUuid = {}, uuidGroups = {};
                  let distinctCount = 0;

                  records.forEach((r, i) => {
                    const uuid = r.record_uuid || "unknown";
                    if (!(uuid in colorByUuid)) {
                      colorByUuid[uuid] = GROUP_COLORS[distinctCount % GROUP_COLORS.length];
                      distinctCount += 1;
                    }
                    if (!uuidGroups[uuid]) uuidGroups[uuid] = { indices: [], firstIndex: i, lastIndex: i };
                    uuidGroups[uuid].indices.push(i);
                    uuidGroups[uuid].lastIndex = i;
                  });

                  return (
                    <React.Fragment key={index}>
                      <TableRow onClick={() => handleExpandClick(index)} sx={{ cursor: "pointer", transition: "all 0.2s ease", backgroundColor: "#fff", "&:hover": { backgroundColor: "#F0F8FF" } }}>
                        <TableCell sx={{ position: "sticky", left: 0, zIndex: isExpanded ? 6 : 4, backgroundColor: "#fff", borderBottom: isExpanded ? "4px solid #c3c3c5ff" : "none",
                          borderRight: "1px solid #E5E7EB", ...(isExpanded && { position: "sticky", top: 56, zIndex: 6 }) }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <IconButton size="small" sx={{ backgroundColor: isExpanded ? "#EEF2FF" : "#F3F4F6", transition: "all 0.2s ease",
                              "&:hover": { backgroundColor: isExpanded ? "#DDD6FE" : "#E5E7EB", transform: "scale(1.1)" } }}>
                              {isExpanded ? <ExpandLess fontSize="small" sx={{ color: "#6366F1" }} /> : <ExpandMore fontSize="small" sx={{ color: "#6B7280" }} />}
                            </IconButton>
                            <Typography sx={{ fontWeight: 600, fontSize: 15, color: "#111827" }}>{patient.patient_name}</Typography>
                            <Box sx={{ flexGrow: 1 }} />
                          </Box>
                        </TableCell>
                        {[...Array(5)].map((_, i) => (
                          <TableCell key={i} sx={{ borderBottom: isExpanded ? "4px solid #c3c3c5ff" : "none", borderRight: i < 4 ? "1px solid #E5E7EB" : "none",
                            ...(isExpanded && { position: "sticky", top: 56, zIndex: 6, backgroundColor: "#fff" }) }} />
                        ))}
                      </TableRow>

                      {isExpanded && records.map((rec, i) => {
                        const uuid = rec.record_uuid || "unknown", bg = colorByUuid[uuid];
                        const currentUuidIndex = records.slice(0, i + 1).filter((r) => (r.record_uuid || "unknown") === uuid).length;
                        const dates = Array.isArray(rec.encounter_date) ? rec.encounter_date : rec.encounter_date ? [rec.encounter_date] : [];
                        const rowKey = `${index}_${i}`, isLastRecord = i === records.length - 1;

                        return (
                          <Fade in={isExpanded} key={rowKey} timeout={300}>
                            <TableRow sx={{ backgroundColor: bg, transition: "all 0.3s ease", borderBottom: isLastRecord ? "1px solid #F3F4F6" : `1px solid ${bg}`,
                              "&:hover": { backgroundColor: bg, filter: "brightness(0.97)" } }}>
                              <TableCell sx={{ position: "sticky", left: 0, zIndex: 4, backgroundColor: bg, py: 2, borderBottom: "1px solid #E5E7EB", borderRight: "1px solid #E5E7EB" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center", position: "relative", height: "100%" }}>
                                  <Box sx={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#3B82F6" }}>{`#${currentUuidIndex}`}</Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ py: 2, borderRight: "1px solid #E5E7EB" }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1F2937" }}>{rec.provider}</Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2, textAlign: rec.diagnosis ? "left" : "center", borderRight: "1px solid #E5E7EB" }}>
                                {rec.diagnosis ? <Typography sx={{ fontSize: 14, color: "#374151" }}>{rec.diagnosis}</Typography> : <EmptyIndicator />}
                              </TableCell>
                              <TableCell sx={{ py: 2, textAlign: rec.drug_brand_name ? "left" : "center", borderRight: "1px solid #E5E7EB" }}>
                                {rec.drug_brand_name ? <Typography sx={{ fontSize: 14, color: "#374151" }}>{rec.drug_brand_name}</Typography> : 
                                  <EmptyIndicator color="#292f3fff" iconColor="#ffffffff" />}
                              </TableCell>
                              <TableCell sx={{ py: 2, borderRight: "1px solid #E5E7EB", textAlign: "center" }}>
                                <Chip label={rec.count || "0"} size="small" sx={{ height: 26, fontSize: 13, fontWeight: 600, backgroundColor: "#6B7280",
                                  color: "white", border: "1px solid #E5E7EB" }} />
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <DateTimeDisplay dates={dates} rowKey={rowKey} isExpanded={expandedDates[rowKey]} onToggle={toggleDateExpansion} />
                              </TableCell>
                            </TableRow>
                          </Fade>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default PatientEncountersTable;